// T-347 (collettiva): "Scarico merce" della giornata produttiva.
// Rimpiazza il vecchio flusso Impegna/Chiudi (T-278) col modello deciso
// dall'owner: lo CHEF prepara le righe (lotto FEFO + qtà) e **SALVA** la bozza
// (NON scarica); il RESPONSABILE (owner/consulente/ristoratore) rilegge, corregge
// e **SCARICA & CHIUDE** (emette gli `stock_out` + segna la bozza `closed`).
// - Righe pre-caricate dal fabbisogno del piano del giorno (netto ricetta ×
//   porzioni), con lotto pre-selezionato **FEFO** (scadenza più vicina) e qtà
//   editabile — stessa logica del flusso Materia prima delle etichette (T-240).
// - Selezione lotto per riga (scadenza + disponibilità mostrate); qtà editabile.
// - **Righe personalizzate**: articolo mancante, oppure split su un altro lotto
//   quando il primo si esaurisce.
// - Bozza persistita in `tenants.settings.collettiva_discharge` (sincronizzata),
//   così lo chef salva su un device e il responsabile chiude su un altro.
// Append-only su hash chain (eventi `stock_out`, come le altre pagine stock).
import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonSearchbar,
  IonLabel,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { saveOutline, checkmarkDoneOutline, trashOutline, checkmarkCircle, chevronForwardOutline, chevronDownOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CollettivaDischargeLine,
  Recipe,
  RecipeIngredient,
  ReversibleStockMovementV1,
  StockCommandMasterEffectV1,
  StockCommandV1,
  StockConversionSource,
  StockSourceLineV1,
} from '@trackeat/shared';
import {
  SUPPLY_REQUEST_MEAL_SERVICES,
  SUPPLY_REQUEST_MEAL_SERVICE_LABELS,
  StockCommandV1Schema,
  type SupplyRequestMealService,
  type IngredientConsumption,
  computeRecipeConsumption,
  aggregateConsumption,
  buildDishIngredientInputs,
  aggregateInputAllergens,
  convertQty,
  type DischargeLotRef,
} from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useCollettivaEnabled,
  useEnqueueStockCommand,
  useEvents,
  useMasters,
  useStockProjection,
  useCollettivaDischargeDraft,
  useSetCollettivaDischargeDraft,
  collettivaDischargeKey,
} from '../../lib/data/queries.js';
import { SearchPickerModal, PickerTriggerField, type SearchPickerItem } from '../../components/SearchPickerModal.js';
import { projectedCarryingCost } from '../../lib/stockCarryingCost.js';
import { buildProductionTraceResetEffects } from '../../lib/productionTraceReset.js';
import { getSupabase } from '../../lib/supabase.js';
import {
  productionSheetIsClosed,
  productionSheetNeedsDischargeReconciliation,
} from '../../lib/productionSheetClosure.js';

interface PlanItemRow {
  id: string;
  recipe_id: string | null;
  output_name: string;
  category: string | null;
  planned_portions: number;
  status: 'pending' | 'done';
  meal_service: string;
  diet_code: string | null; // T-441: dieta della riga (base/variante)
}

type RecipeRow = Pick<Recipe, 'id' | 'name'> & {
  ingredients?: RecipeIngredient[];
  // T-441: se valorizzato, la ricetta risolta è una VARIANTE (ricetta figlia).
  variant_of_recipe_id?: string | null;
};
type FoodRow = {
  id: string;
  name: string;
  default_uom?: string | null;
  current_batch_lot?: string | null;
  allergens?: string[] | null;
};

// T-452: vista minima produzione per riscrivere gli inputs di tracciabilità (QR)
// alla chiusura/salvataggio dello scarico.
interface ProductionRow {
  id: string;
  source_production_plan_item_id?: string | null;
  version?: number | null;
  archived_at?: string | null;
}

// Lotto disponibile per un alimento (derivato dagli eventi stock_in − stock_out).
interface LotEntry {
  batch_lot: string | null;
  expiration_date: string | null;
  available: number;
  uom: string;
  supplier_id: string | null;
  document_id: string | null;
  storage_unit_id: string | null;
  unit_cost: number | null;
  currency: string;
}

// Riga editabile di scarico (qty come stringa per l'input).
interface DischargeLineUI {
  key: string;
  food_id: string;
  food_name: string;
  batch_lot: string | null;
  expiration_date: string | null;
  qty: string;
  uom: string;
  custom: boolean;
}

interface LocalStockCommandEvent {
  id?: string | null;
  server_id?: string | null;
  business_id?: string | null;
  kind: string;
  rev?: number | null;
  client_ts?: string | null;
  server_ts?: string | null;
  payload: Record<string, unknown>;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CONVERSION_SOURCES = new Set<StockConversionSource>([
  'identity',
  'metric',
  'document_pack',
  'supplier_alias',
  'user_confirmed',
  'carried',
]);

function normalizedUom(value: string): string {
  return value.trim().toLowerCase();
}

function productionInputLine(input: {
  foodId: string;
  batchLot: string | null;
  expirationDate: string | null;
  supplierId: string | null;
  documentId: string | null;
  storageUnitId: string | null;
  qty: number;
  sourceUom: string;
  stockUom: string;
  unitCost: number | null;
  currency: string;
  lineRef: string;
  operatorName: string | null;
}): StockSourceLineV1 {
  const sourceUom = input.sourceUom.trim();
  const stockUom = input.stockUom.trim();
  const factor = convertQty(1, sourceUom, stockUom);
  if (factor == null || factor <= 0) {
    throw new Error(`UdM incompatibili per lo scarico: ${sourceUom} → ${stockUom}`);
  }
  const sourceUnitPrice = input.unitCost == null ? null : input.unitCost * factor;
  return {
    food_id: input.foodId,
    batch_lot: input.batchLot,
    expiration_date: input.expirationDate,
    supplier_id: input.supplierId,
    document_id: input.documentId,
    storage_unit_id: input.storageUnitId,
    line_ref: input.lineRef,
    lot_action: 'recorded_only',
    operator_name: input.operatorName,
    source_qty: input.qty,
    source_uom: sourceUom,
    source_unit_price: sourceUnitPrice,
    source_line_total: sourceUnitPrice == null ? null : sourceUnitPrice * input.qty,
    currency: input.currency,
    stock_uom: stockUom,
    conversion: {
      factor,
      source: normalizedUom(sourceUom) === normalizedUom(stockUom) ? 'identity' : 'metric',
    },
  };
}

function eventBusinessId(event: LocalStockCommandEvent): string | null {
  const payloadBusinessId = event.payload.business_id;
  return event.business_id
    ?? (typeof payloadBusinessId === 'string' ? payloadBusinessId : null);
}

function compareEventRecency(
  left: LocalStockCommandEvent,
  right: LocalStockCommandEvent,
): number {
  const leftRev = typeof left.rev === 'number' && Number.isFinite(left.rev) ? left.rev : null;
  const rightRev = typeof right.rev === 'number' && Number.isFinite(right.rev) ? right.rev : null;
  if (leftRev !== null && rightRev !== null && leftRev !== rightRev) return leftRev - rightRev;
  if (leftRev === null && rightRev !== null) return 1;
  if (leftRev !== null && rightRev === null) return -1;
  const leftTs = Date.parse(left.server_ts ?? left.client_ts ?? '') || 0;
  const rightTs = Date.parse(right.server_ts ?? right.client_ts ?? '') || 0;
  return leftTs - rightTs;
}

function latestActiveProductionCommandId(
  events: readonly LocalStockCommandEvent[],
  draftKey: string,
  businessId: string,
): string | null {
  const reversed = new Set<string>();
  for (const event of events) {
    if (eventBusinessId(event) !== businessId) continue;
    const target = event.payload.reverses_command_id;
    if (event.payload.movement_class === 'reverse' && typeof target === 'string') {
      reversed.add(target);
    }
  }

  const prefix = `collettiva:${draftKey}:`;
  const candidates = new Map<string, LocalStockCommandEvent>();
  for (const event of events) {
    if (eventBusinessId(event) !== businessId) continue;
    const payload = event.payload;
    const commandId = payload.command_id;
    const lineRef = payload.source_line_ref;
    if (
      payload.movement_class !== 'production'
      || typeof commandId !== 'string'
      || !UUID_PATTERN.test(commandId)
      || typeof lineRef !== 'string'
      || !lineRef.startsWith(prefix)
      || reversed.has(commandId)
    ) {
      continue;
    }
    const current = candidates.get(commandId);
    if (!current || compareEventRecency(current, event) < 0) candidates.set(commandId, event);
  }

  let latest: [string, LocalStockCommandEvent] | null = null;
  for (const candidate of candidates) {
    if (!latest || compareEventRecency(latest[1], candidate[1]) < 0) latest = candidate;
  }
  return latest?.[0] ?? null;
}

function hasProductionCommandHistory(
  events: readonly LocalStockCommandEvent[],
  draftKey: string,
  businessId: string,
): boolean {
  const prefix = `collettiva:${draftKey}:`;
  return events.some((event) => (
    eventBusinessId(event) === businessId
    && event.payload.movement_class === 'production'
    && typeof event.payload.command_id === 'string'
    && typeof event.payload.source_line_ref === 'string'
    && event.payload.source_line_ref.startsWith(prefix)
  ));
}

function requiredPositiveNumber(payload: Record<string, unknown>, key: string): number {
  const value = payload[key];
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new Error(`Movimento originale incompleto: ${key}`);
  }
  return value;
}

function nullableNonNegativeNumber(
  payload: Record<string, unknown>,
  key: string,
): number | null {
  const value = payload[key];
  if (value == null) return null;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error(`Movimento originale non valido: ${key}`);
  }
  return value;
}

function nullableUuid(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && UUID_PATTERN.test(value) ? value : null;
}

function reverseMovementFromEvent(event: LocalStockCommandEvent): ReversibleStockMovementV1 {
  const payload = event.payload;
  const originalSeq = payload.command_seq;
  const foodId = payload.food_id;
  const sourceUom = payload.source_uom;
  const stockUom = payload.stock_uom;
  const sourceCurrency = payload.source_currency;
  const conversionFactor = payload.conversion_factor;
  const conversionSource = payload.conversion_source;
  const businessId = eventBusinessId(event);
  if (
    (event.kind !== 'stock_in' && event.kind !== 'stock_out')
    || typeof originalSeq !== 'number'
    || !Number.isInteger(originalSeq)
    || originalSeq <= 0
    || typeof foodId !== 'string'
    || typeof sourceUom !== 'string'
    || typeof stockUom !== 'string'
    || typeof sourceCurrency !== 'string'
    || typeof conversionFactor !== 'number'
    || !Number.isFinite(conversionFactor)
    || conversionFactor <= 0
    || typeof conversionSource !== 'string'
    || !CONVERSION_SOURCES.has(conversionSource as StockConversionSource)
    || businessId == null
  ) {
    throw new Error('Il comando originale non contiene dati sufficienti per uno storno sicuro');
  }
  const referenceId = payload.conversion_reference_id;
  const sourceLineRef = payload.source_line_ref;
  const clientEventId = event.id;
  if (typeof clientEventId !== 'string' || !UUID_PATTERN.test(clientEventId)) {
    throw new Error('Identificativo locale del movimento originale non valido');
  }
  const serverEventId = event.server_id;
  if (typeof serverEventId !== 'string' || !UUID_PATTERN.test(serverEventId)) {
    throw new Error('Sincronizza i movimenti prima di riaprire la scheda di produzione');
  }
  return {
    food_id: foodId,
    batch_lot: typeof payload.batch_lot === 'string' ? payload.batch_lot : null,
    expiration_date: typeof payload.expiration_date === 'string' ? payload.expiration_date : null,
    supplier_id: nullableUuid(payload, 'supplier_id'),
    document_id: nullableUuid(payload, 'document_id'),
    storage_unit_id: nullableUuid(payload, 'storage_unit_id'),
    line_ref: typeof sourceLineRef === 'string' ? sourceLineRef : null,
    original_event_id: serverEventId,
    original_event_client_uuid: clientEventId,
    original_seq: originalSeq,
    kind: event.kind,
    business_id: businessId,
    source_qty: requiredPositiveNumber(payload, 'source_qty'),
    source_uom: sourceUom,
    source_unit_price: nullableNonNegativeNumber(payload, 'source_unit_price'),
    source_line_total: nullableNonNegativeNumber(payload, 'source_line_total'),
    currency: sourceCurrency,
    stock_qty: requiredPositiveNumber(payload, 'stock_qty'),
    stock_uom: stockUom,
    unit_cost: nullableNonNegativeNumber(payload, 'stock_unit_cost'),
    conversion: {
      factor: conversionFactor,
      source: conversionSource as StockConversionSource,
      ...(typeof referenceId === 'string' ? { reference_id: referenceId } : {}),
    },
  };
}

function reverseMovementsForCommand(
  events: readonly LocalStockCommandEvent[],
  commandId: string,
  businessId: string,
): ReversibleStockMovementV1[] {
  const originalEvents = events
    .filter((event) => (
      eventBusinessId(event) === businessId
      && event.payload.movement_class === 'production'
      && event.payload.command_id === commandId
    ))
    .sort((left, right) => (
      Number(left.payload.command_seq) - Number(right.payload.command_seq)
    ));
  if (originalEvents.length === 0) {
    throw new Error('Comando di produzione originale non trovato');
  }
  return originalEvents.map(reverseMovementFromEvent);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('it', { day: '2-digit', month: '2-digit' });
}

function fmtNum(n: number): string {
  return n.toLocaleString('it', { maximumFractionDigits: 3 });
}

// Valore stringa del <select> lotto: usiamo il batch_lot (o '__none__').
const NONE_LOT = '__none__';

export function ProductionSheetPage(): JSX.Element {
  const { profile } = useAuth();
  const collettivaEnabled = useCollettivaEnabled();
  const { data: recipes = [] } = useMasters<RecipeRow>('recipes');
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const { data: productions = [] } = useMasters<ProductionRow>('productions');
  const { data: stockIn = [] } = useEvents('stock_in', null);
  const { data: stockOut = [] } = useEvents('stock_out', null);
  const canonicalUomByFood = useMemo(() => Object.fromEntries(
    foods
      .filter((food) => typeof food.default_uom === 'string' && food.default_uom.trim() !== '')
      .map((food) => [food.id, food.default_uom!.trim()]),
  ), [foods]);
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });
  const enqueueStockCommand = useEnqueueStockCommand();
  const setDraft = useSetCollettivaDischargeDraft();
  const sb = getSupabase();

  // Il responsabile (owner/consulente/ristoratore) può scaricare & chiudere;
  // lo chef (staff) e gli altri ruoli possono solo SALVARE la bozza.
  const canDischarge =
    profile?.is_platform_owner === true ||
    profile?.role === 'consulente' ||
    profile?.role === 'ristoratore';

  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [mealService, setMealService] = useState<SupplyRequestMealService>('pranzo');
  const [items, setItems] = useState<PlanItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | 'discharge' | 'reopen'>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [lines, setLines] = useState<DischargeLineUI[]>([]);
  const [dirty, setDirty] = useState(false);
  // T-457: ricerca per trovare velocemente un prodotto tra le righe di scarico.
  const [lineSearch, setLineSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openLotKey, setOpenLotKey] = useState<string | null>(null);
  const pendingCompound = useRef<{
    key: string;
    command: StockCommandV1;
    masterEffects: readonly StockCommandMasterEffectV1[];
    draftLines?: readonly CollettivaDischargeLine[];
  } | null>(null);

  const businessId = profile?.active_business_id ?? null;
  const draftKey = businessId ? collettivaDischargeKey(businessId, serviceDate, mealService) : null;
  const savedDraft = useCollettivaDischargeDraft(draftKey);
  const stockCommandEvents = useMemo(
    () => [...stockIn, ...stockOut] as LocalStockCommandEvent[],
    [stockIn, stockOut],
  );
  const activeDischargeCommandId = useMemo(
    () => (
      draftKey && businessId
        ? latestActiveProductionCommandId(stockCommandEvents, draftKey, businessId)
        : null
    ),
    [businessId, draftKey, stockCommandEvents],
  );
  const hasVersionedDischarge = useMemo(
    () => (
      draftKey && businessId
        ? hasProductionCommandHistory(stockCommandEvents, draftKey, businessId)
        : false
    ),
    [businessId, draftKey, stockCommandEvents],
  );
  // Dal primo comando versionato in poi lo stato autoritativo è la storia
  // append-only: il flag nella bozza resta soltanto una cache UI best-effort.
  const closed = productionSheetIsClosed({
    activeCommandId: activeDischargeCommandId,
    hasVersionedEventHistory: hasVersionedDischarge,
    draftClosed: savedDraft?.closed === true,
    draftStockCommandId: savedDraft?.stock_command_id,
  });
  const dischargeNeedsReconciliation =
    productionSheetNeedsDischargeReconciliation({
      activeCommandId: activeDischargeCommandId,
      hasVersionedEventHistory: hasVersionedDischarge,
      draftClosed: savedDraft?.closed === true,
      draftStockCommandId: savedDraft?.stock_command_id,
    });

  const recipeById = useMemo(() => {
    const m = new Map<string, RecipeRow>();
    recipes.forEach((r) => m.set(r.id, r));
    return m;
  }, [recipes]);
  const foodById = useMemo(() => {
    const m = new Map<string, FoodRow>();
    foods.forEach((f) => m.set(f.id, f));
    return m;
  }, [foods]);

  // Consumo netto per alimento dal piano del giorno (grammatura ricetta × porzioni).
  // T-441 — la ricetta risolta per id (it.recipe_id) è già la VARIANTE quando
  // definita → i suoi ingredienti sono quelli giusti. Casi particolari:
  //  - pending (recipe_id=base + diet_code): variante non ancora definita →
  //    si consuma il BOM della BASE come STIMA (non si blocca); i food coinvolti
  //    vengono marcati per la nota UI "stima su base".
  //  - variante attesa ma non ancora sincronizzata (recipeById.get = undefined):
  //    la riga è ESCLUSA dal consumo (non si consuma la base al suo posto) e si
  //    segnala "in sincronizzazione".
  const { consumption, estimateFoodIds, hasSyncingRows } = useMemo(() => {
    const rows: IngredientConsumption[] = [];
    const estimate = new Set<string>();
    let syncing = false;
    for (const it of items) {
      if (!it.recipe_id || it.planned_portions <= 0) continue;
      const recipe = recipeById.get(it.recipe_id);
      if (recipe === undefined) {
        // Variante attesa ma non sincronizzata → NON consumare la base.
        if (it.diet_code != null) syncing = true;
        continue;
      }
      if (!recipe.ingredients) continue;
      const consumed = computeRecipeConsumption(recipe.ingredients, it.planned_portions);
      // Pending: la riga ha una dieta ma la ricetta risolta è la BASE (variante
      // non ancora definita) → stima sul BOM base.
      const isEstimate = it.diet_code != null && recipe.variant_of_recipe_id == null;
      if (isEstimate) for (const c of consumed) estimate.add(c.food_id);
      rows.push(...consumed);
    }
    const aggregated = aggregateConsumption(rows).sort((a, b) => {
      const an = foodById.get(a.food_id)?.name ?? '';
      const bn = foodById.get(b.food_id)?.name ?? '';
      return an.localeCompare(bn, 'it');
    });
    return { consumption: aggregated, estimateFoodIds: estimate, hasSyncingRows: syncing };
  }, [items, recipeById, foodById]);

  // Lotti disponibili dal projector canonico (storico completo). Gli stock-in
  // grezzi servono soltanto per metadata: quantità e costo medio carried
  // vengono dalla stessa proiezione, anche per prodotti e trasferimenti.
  const lotsByFood = useMemo(() => {
    type LotMetadata = Pick<
      LotEntry,
      'supplier_id' | 'document_id' | 'storage_unit_id'
    >;
    const metadata = new Map<string, LotMetadata>();
    for (const event of stockIn as LocalStockCommandEvent[]) {
      if (eventBusinessId(event) !== businessId) continue;
      const payload = event.payload;
      const foodId = typeof payload.food_id === 'string' ? payload.food_id : null;
      if (!foodId) continue;
      const batchLot = typeof payload.batch_lot === 'string' ? payload.batch_lot : null;
      const key = `${foodId}|${batchLot ?? ''}`;
      const current = metadata.get(key) ?? {
        supplier_id: null,
        document_id: null,
        storage_unit_id: null,
      };
      current.supplier_id ??= nullableUuid(payload, 'supplier_id');
      current.document_id ??= nullableUuid(payload, 'document_id');
      current.storage_unit_id ??= nullableUuid(payload, 'storage_unit_id');
      metadata.set(key, current);
    }

    const out = new Map<string, LotEntry[]>();
    for (const balance of stockProjection.balances) {
      if (balance.availableQty <= 0.0001 || balance.closed) continue;
      const key = `${balance.foodId}|${balance.batchLot ?? ''}`;
      const carried = metadata.get(key);
      const carryingCost = projectedCarryingCost(balance);
      const list = out.get(balance.foodId) ?? [];
      list.push({
        batch_lot: balance.batchLot,
        expiration_date: balance.expirationDate,
        available: balance.availableQty,
        uom: balance.uom,
        supplier_id: carried?.supplier_id ?? null,
        document_id: carried?.document_id ?? null,
        storage_unit_id: carried?.storage_unit_id ?? null,
        unit_cost: carryingCost.unitCost,
        currency: carryingCost.currency,
      });
      list.sort((a, b) => {
        const ea = a.expiration_date ? Date.parse(a.expiration_date) : Infinity;
        const eb = b.expiration_date ? Date.parse(b.expiration_date) : Infinity;
        return ea - eb; // FEFO
      });
      out.set(balance.foodId, list);
    }
    return out;
  }, [businessId, stockIn, stockProjection.balances]);

  // Righe di default dal fabbisogno del piano (lotto FEFO pre-selezionato).
  const defaultLines = useMemo<DischargeLineUI[]>(() => {
    return consumption.map((c) => {
      const lots = lotsByFood.get(c.food_id) ?? [];
      const lot = lots[0];
      const food = foodById.get(c.food_id);
      return {
        key: `plan:${c.food_id}`,
        food_id: c.food_id,
        food_name: food?.name ?? c.food_id.slice(0, 8),
        batch_lot: lot?.batch_lot ?? food?.current_batch_lot ?? null,
        expiration_date: lot?.expiration_date ?? null,
        qty: fmtNum(c.qty),
        uom: lot?.uom ?? c.uom,
        custom: false,
      };
    });
  }, [consumption, lotsByFood, foodById]);

  // T-457: righe filtrate dalla barra di ricerca (per nome prodotto).
  const visibleLines = useMemo(() => {
    const q = lineSearch.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter((l) => l.food_name.toLowerCase().includes(q));
  }, [lines, lineSearch]);

  // T-452: plan_item_id → produzione del giorno (le produzioni del piano portano
  // source_production_plan_item_id). Serve a riscrivere gli inputs del QR.
  const prodByPlanItem = useMemo(() => {
    const m = new Map<string, ProductionRow>();
    for (const p of productions) {
      if (p.archived_at || !p.source_production_plan_item_id) continue;
      m.set(p.source_production_plan_item_id, p);
    }
    return m;
  }, [productions]);

  // Elenchi per il selettore alimenti con ricerca (aggiungi riga manuale).
  const foodPickerItems = useMemo<SearchPickerItem[]>(
    () =>
      [...foods]
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'it'))
        .map((f) => {
          const lots = lotsByFood.get(f.id) ?? [];
          const tot = lots.reduce((a, l) => a + l.available, 0);
          return {
            id: f.id,
            label: f.name,
            sublabel: lots.length > 0
              ? `disp ${fmtNum(tot)} ${lots[0]?.uom ?? f.default_uom ?? ''}`
              : 'nessun lotto a magazzino',
          } as SearchPickerItem;
        }),
    [foods, lotsByFood],
  );

  // Carica le righe di piano dal DB per (data, servizio).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId || !businessId) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const { data, error } = await sb
          .from('production_plan_items')
          .select('id,recipe_id,output_name,category,planned_portions,status,meal_service,diet_code')
          .eq('tenant_id', tenantId)
          .eq('business_id', businessId)
          .eq('service_date', serviceDate)
          .eq('meal_service', mealService)
          .is('archived_at', null)
          .order('category', { ascending: true });
        if (error) throw error;
        if (!cancelled) setItems((data ?? []) as PlanItemRow[]);
      } catch (err) {
        if (!cancelled) setToast(toUserMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.active_tenant_id, businessId, serviceDate, mealService]);

  // Cambio giorno/servizio → reset "sporco" (permette il re-seed dal default).
  useEffect(() => {
    setDirty(false);
    setPickerOpen(false);
    setOpenLotKey(null);
  }, [draftKey]);

  // Seed delle righe: preferisci la BOZZA SALVATA (il lavoro dello chef, o lo
  // stato chiuso) così il responsabile la rilegge; in assenza di bozza usa il
  // fabbisogno del piano (lotto FEFO). Appena l'utente edita (dirty) smetti di
  // sovrascrivere, così non si perdono le sue modifiche.
  useEffect(() => {
    if (dirty) return;
    if (savedDraft) {
      setLines(
        savedDraft.lines.map((l, i) => ({
          key: `saved:${i}`,
          food_id: l.food_id,
          food_name: l.food_name,
          batch_lot: l.batch_lot,
          expiration_date: l.expiration_date,
          qty: fmtNum(l.qty),
          uom: l.uom,
          custom: l.custom,
        })),
      );
    } else {
      setLines(defaultLines);
    }
  }, [savedDraft, defaultLines, dirty]);

  const patchLine = useCallback((key: string, p: Partial<DischargeLineUI>): void => {
    setDirty(true);
    setLines((cur) => cur.map((l) => (l.key === key ? { ...l, ...p } : l)));
  }, []);

  const removeLine = useCallback((key: string): void => {
    setDirty(true);
    setLines((cur) => cur.filter((l) => l.key !== key));
  }, []);

  function onSelectLot(key: string, value: string): void {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    if (value === NONE_LOT) {
      patchLine(key, { batch_lot: null, expiration_date: null });
      return;
    }
    const lot = (lotsByFood.get(line.food_id) ?? []).find((l) => (l.batch_lot ?? '') === value);
    patchLine(key, {
      batch_lot: lot?.batch_lot ?? value,
      expiration_date: lot?.expiration_date ?? null,
      // Allinea l'unità di misura a quella del lotto scelto (giacenza reale).
      ...(lot?.uom ? { uom: lot.uom } : {}),
    });
  }

  function addCustomLine(foodId: string): void {
    const food = foodById.get(foodId);
    if (!food) return;
    const lots = lotsByFood.get(foodId) ?? [];
    const lot = lots[0];
    setDirty(true);
    setLines((cur) => [
      ...cur,
      {
        key: `custom:${foodId}:${cur.length}:${cur.filter((l) => l.food_id === foodId).length}`,
        food_id: foodId,
        food_name: food.name,
        batch_lot: lot?.batch_lot ?? food.current_batch_lot ?? null,
        expiration_date: lot?.expiration_date ?? null,
        qty: '',
        uom: lot?.uom ?? food.default_uom ?? 'pz',
        custom: true,
      },
    ]);
    setPickerOpen(false);
  }

  function toModelLines(): CollettivaDischargeLine[] {
    return lines.map((l) => ({
      food_id: l.food_id,
      food_name: l.food_name,
      batch_lot: l.batch_lot,
      expiration_date: l.expiration_date,
      qty: Number((l.qty || '0').replace(',', '.')) || 0,
      uom: l.uom,
      custom: l.custom,
    }));
  }

  // T-452: scrive gli `inputs` di tracciabilità (piatto → ingredienti × lotti dello
  // scarico) sulle produzioni del giorno → il QR mostra la filiera completa.
  // PROVVISORIO (da doSave, anche operatore) o FISSATO (da doDischarge, solo
  // responsabile): stessa funzione, cambia solo QUANDO viene chiamata. Il fissaggio
  // vive dentro doDischarge (gated canDischarge) → un operatore non può fissare.
  function productionInputEffects(
    lines: Array<{ food_id: string; batch_lot: string | null; expiration_date: string | null; qty: number }>,
  ): StockCommandMasterEffectV1[] {
    const tenantId = profile?.active_tenant_id;
    if (!tenantId) return [];
    // Lotti scaricati per ingrediente (multi-lotto): righe con qty>0, dedup per lotto.
    const dischargeByFood = new Map<string, DischargeLotRef[]>();
    for (const l of lines) {
      if (l.qty <= 0) continue;
      const arr = dischargeByFood.get(l.food_id) ?? [];
      if (!arr.some((x) => x.batch_lot === (l.batch_lot ?? null))) {
        arr.push({ batch_lot: l.batch_lot ?? null, expiration_date: l.expiration_date ?? null });
      }
      dischargeByFood.set(l.food_id, arr);
    }
    const meta = {
      foodName: (id: string) => foodById.get(id)?.name ?? id,
      allergens: (id: string) => foodById.get(id)?.allergens ?? [],
    };
    const effects: StockCommandMasterEffectV1[] = [];
    for (const it of items) {
      if (!it.recipe_id) continue;
      const prod = prodByPlanItem.get(it.id);
      if (!prod) continue;
      const recipe = recipeById.get(it.recipe_id);
      if (!recipe?.ingredients) continue;
      const demand = computeRecipeConsumption(recipe.ingredients, it.planned_portions);
      const inputs = buildDishIngredientInputs(demand, dischargeByFood, meta);
      effects.push({
        table: 'productions',
        id: prod.id,
        op: 'update',
        base_version: prod.version ?? 0,
        patch: {
          id: prod.id,
          tenant_id: tenantId,
          inputs,
          allergens_aggregated: aggregateInputAllergens(inputs),
        },
      });
    }
    return effects;
  }

  function productionInputResetEffects(): StockCommandMasterEffectV1[] {
    const tenantId = profile?.active_tenant_id;
    if (!tenantId) return [];
    const targets: ProductionRow[] = [];
    for (const item of items) {
      if (!item.recipe_id) continue;
      const production = prodByPlanItem.get(item.id);
      const recipe = recipeById.get(item.recipe_id);
      if (!production || !recipe?.ingredients) continue;
      targets.push(production);
    }
    return buildProductionTraceResetEffects(targets, tenantId);
  }

  async function doSave(): Promise<void> {
    const tenantId = profile?.active_tenant_id;
    if (!draftKey || !businessId || !tenantId) {
      setToast('Seleziona una sede');
      return;
    }
    setBusy(true);
    try {
      const model = toModelLines();
      const pendingKey = `save:${draftKey}`;
      let compound = pendingCompound.current?.key === pendingKey
        ? pendingCompound.current
        : null;
      if (!compound) {
        const masterEffects = productionInputEffects(model);
        if (masterEffects.length > 0) {
          compound = {
            key: pendingKey,
            command: StockCommandV1Schema.parse({
              version: 1,
              command_id: crypto.randomUUID(),
              tenant_id: tenantId,
              movement_class: 'production',
              economic_effect: 'internal',
              business_id: businessId,
              reason: `collettiva_trace_draft:${serviceDate}:${mealService}`,
              inputs: [],
              outputs: [],
            }),
            masterEffects,
          };
          pendingCompound.current = compound;
        }
      }
      if (compound) {
        await enqueueStockCommand.mutateAsync({
          command: compound.command,
          master_effects: compound.masterEffects,
        });
        pendingCompound.current = null;
      }
      await setDraft.mutateAsync({
        key: draftKey,
        draft: {
          lines: model,
          closed: false,
          stock_command_id: null,
          saved_by: profile?.id ?? null,
          saved_at: new Date().toISOString(),
          closed_by: null,
          closed_at: null,
        },
      });
      setToast('Bozza di scarico salvata');
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function doDischarge(): Promise<void> {
    const tenantId = profile?.active_tenant_id;
    if (!draftKey || !businessId || !tenantId) {
      setToast('Seleziona una sede');
      return;
    }
    if (!canDischarge) {
      setToast('Solo il responsabile può scaricare e chiudere il modulo');
      return;
    }
    setBusy(true);
    try {
      const pendingKey = `discharge:${draftKey}`;
      const pending = pendingCompound.current?.key === pendingKey
        ? pendingCompound.current
        : null;
      if (pending) {
        await enqueueStockCommand.mutateAsync({
          command: pending.command,
          master_effects: pending.masterEffects,
        });
        pendingCompound.current = null;
        const replayLines = [...(pending.draftLines ?? savedDraft?.lines ?? toModelLines())];
        try {
          await setDraft.mutateAsync({
            key: draftKey,
            draft: {
              lines: replayLines,
              closed: true,
              stock_command_id: pending.command.command_id,
              saved_by: savedDraft?.saved_by ?? profile?.id ?? null,
              saved_at: savedDraft?.saved_at ?? new Date().toISOString(),
              closed_by: profile?.id ?? null,
              closed_at: new Date().toISOString(),
            },
          });
        } catch {
          // La storia StockCommand è autoritativa; il flag bozza si riallinea
          // al prossimo salvataggio senza riemettere movimenti.
        }
        setToast(`Scaricate ${replayLines.filter((line) => line.qty > 0).length} righe · modulo chiuso`);
        return;
      }

      const allModel = toModelLines();
      const model = allModel.filter((l) => l.qty > 0);
      if (model.length === 0) throw new Error('Nessuna quantità da scaricare');

      const requestedByLot = new Map<string, number>();
      const commandInputs = model.map((line, index) => {
        const food = foodById.get(line.food_id);
        const lot = (lotsByFood.get(line.food_id) ?? []).find(
          (candidate) => candidate.batch_lot === line.batch_lot,
        );
        if (!lot) {
          throw new Error(`Nessuna giacenza disponibile per ${line.food_name}`);
        }
        const stockUom = food?.default_uom?.trim() || lot.uom || line.uom;
        const factor = convertQty(1, line.uom, stockUom);
        if (factor == null) {
          throw new Error(`UdM incompatibile per ${line.food_name}: ${line.uom} → ${stockUom}`);
        }
        const key = `${line.food_id}|${line.batch_lot ?? ''}`;
        const requested = (requestedByLot.get(key) ?? 0) + line.qty * factor;
        if (requested > lot.available + 0.000_000_001) {
          throw new Error(`Disponibilità insufficiente per ${line.food_name}`);
        }
        requestedByLot.set(key, requested);
        return productionInputLine({
          foodId: line.food_id,
          batchLot: line.batch_lot,
          expirationDate: line.expiration_date,
          supplierId: lot.supplier_id,
          documentId: lot.document_id,
          storageUnitId: lot.storage_unit_id,
          qty: line.qty,
          sourceUom: line.uom,
          stockUom,
          unitCost: lot.unit_cost,
          currency: lot.currency,
          lineRef: `collettiva:${draftKey}:input:${index + 1}`,
          operatorName: profile.full_name?.trim() || null,
        });
      });
      const command: StockCommandV1 = StockCommandV1Schema.parse({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: tenantId,
        movement_class: 'production',
        economic_effect: 'internal',
        business_id: businessId,
        reason: `collettiva_discharge:${serviceDate}:${mealService}`,
        inputs: commandInputs,
        outputs: [],
      });
      const masterEffects = productionInputEffects(model);
      pendingCompound.current = {
        key: pendingKey,
        command,
        masterEffects,
        draftLines: allModel,
      };
      await enqueueStockCommand.mutateAsync({
        command,
        master_effects: masterEffects,
      });
      pendingCompound.current = null;
      try {
        await setDraft.mutateAsync({
          key: draftKey,
          draft: {
            lines: allModel,
            closed: true,
            stock_command_id: command.command_id,
            saved_by: savedDraft?.saved_by ?? profile?.id ?? null,
            saved_at: savedDraft?.saved_at ?? new Date().toISOString(),
            closed_by: profile?.id ?? null,
            closed_at: new Date().toISOString(),
          },
        });
      } catch {
        // Il comando + gli update production sono già persistiti insieme.
        // Non ritentare il movimento per un errore della sola cache bozza.
      }
      setToast(`Scaricate ${model.length} righe · modulo chiuso`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // T-432 — Riapertura dello scarico chiuso (solo responsabile), con conferma.
  // Il reverse ricostruisce TUTTE le gambe del command originale: stock_out
  // degli input e, se presenti, stock_in degli output.
  async function doReopen(): Promise<void> {
    const tenantId = profile?.active_tenant_id;
    if (!draftKey || !businessId || !tenantId) {
      setToast('Seleziona una sede');
      return;
    }
    if (!canDischarge) {
      setToast('Solo il responsabile può riaprire lo scarico');
      return;
    }
    setBusy(true);
    try {
      const pendingKey = `reopen:${draftKey}`;
      const pending = pendingCompound.current?.key === pendingKey
        ? pendingCompound.current
        : null;
      if (pending) {
        await enqueueStockCommand.mutateAsync({
          command: pending.command,
          master_effects: pending.masterEffects,
        });
        pendingCompound.current = null;
        try {
          await setDraft.mutateAsync({
            key: draftKey,
            draft: {
              lines: [...(pending.draftLines ?? savedDraft?.lines ?? toModelLines())],
              closed: false,
              stock_command_id:
                savedDraft?.stock_command_id ?? pending.command.command_id,
              saved_by: savedDraft?.saved_by ?? profile?.id ?? null,
              saved_at: savedDraft?.saved_at ?? new Date().toISOString(),
              closed_by: null,
              closed_at: null,
            },
          });
        } catch {
          // Lo storno append-only determina già lo stato aperto.
        }
        setToast('Scarico riaperto · giacenza ripristinata');
        return;
      }

      const originalCommandId = activeDischargeCommandId;
      if (!originalCommandId) {
        throw new Error(
          'Scarico legacy senza comando versionato: sincronizza il dispositivo prima di riaprire',
        );
      }
      const movements = reverseMovementsForCommand(
        stockCommandEvents,
        originalCommandId,
        businessId,
      );
      const reverseCommand: StockCommandV1 = StockCommandV1Schema.parse({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: tenantId,
        movement_class: 'reverse',
        economic_effect: 'reversal',
        reason: `Riapertura scarico collettiva ${serviceDate} ${mealService}`,
        reverses_command_id: originalCommandId,
        movements,
      });
      const draftLines = savedDraft?.lines ?? toModelLines();
      const masterEffects = productionInputResetEffects();
      pendingCompound.current = {
        key: pendingKey,
        command: reverseCommand,
        masterEffects,
        draftLines,
      };
      await enqueueStockCommand.mutateAsync({
        command: reverseCommand,
        master_effects: masterEffects,
      });
      pendingCompound.current = null;
      try {
        await setDraft.mutateAsync({
          key: draftKey,
          draft: {
            lines: draftLines,
            closed: false,
            stock_command_id:
              savedDraft?.stock_command_id ?? originalCommandId,
            saved_by: savedDraft?.saved_by ?? profile?.id ?? null,
            saved_at: savedDraft?.saved_at ?? new Date().toISOString(),
            closed_by: null,
            closed_at: null,
          },
        });
      } catch {
        // Lo stato deriva dal reverse e non dal flag della bozza.
      }
      setToast(
        movements.length > 0
          ? `Scarico riaperto · ${movements.length} movimenti stornati · lotti da riconfermare`
          : 'Scarico riaperto per modifiche',
      );
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const totalPortions = items.reduce((acc, it) => acc + (it.planned_portions || 0), 0);
  const savedByChefBanner =
    !closed && savedDraft && savedDraft.saved_at
      ? `Bozza salvata il ${new Date(savedDraft.saved_at).toLocaleString('it', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
      : null;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Scarico merce</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#dde3ec' } as React.CSSProperties}>
        {!collettivaEnabled && (
          <div style={{ padding: '24px 16px' }}>
            <p style={{ fontSize: 14, color: '#475569' }}>Modulo collettiva non attivo per questo locale.</p>
          </div>
        )}
        {collettivaEnabled && (
          <div style={{ padding: '12px 16px 130px' }}>
            <p style={{ fontSize: 13, color: '#475569', margin: '4px 4px 12px' }}>
              Tutta la merce da scaricare per la giornata produttiva. Lo chef prepara lotti e quantità e{' '}
              <strong>salva</strong>; il responsabile <strong>scarica e chiude</strong> il modulo del giorno.
            </p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <IonItem style={{ flex: 1, borderRadius: 12 }}>
                <IonLabel position="stacked">Giorno</IonLabel>
                <IonInput type="date" value={serviceDate} onIonInput={(ev) => setServiceDate(String(ev.detail.value ?? todayIso()))} />
              </IonItem>
              <IonItem style={{ flex: 1, borderRadius: 12 }}>
                <IonLabel position="stacked">Servizio</IonLabel>
                <IonSelect value={mealService} onIonChange={(ev) => setMealService(ev.detail.value as SupplyRequestMealService)}>
                  {SUPPLY_REQUEST_MEAL_SERVICES.map((s) => (
                    <IonSelectOption key={s} value={s}>{SUPPLY_REQUEST_MEAL_SERVICE_LABELS[s]}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </div>

            {closed && (
              <div style={{ background: '#dcfce7', border: '1px solid #16a34a', borderRadius: 12, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#166534' }}>
                ✅ Modulo <strong>scaricato e chiuso</strong>
                {savedDraft?.closed_at ? ` il ${new Date(savedDraft.closed_at).toLocaleString('it', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}.
                Le righe sotto sono in sola lettura.
                {/* T-349 (audit UX): passo successivo del flusso (step 5) */}
                <IonButton expand="block" size="small" fill="outline" color="success" style={{ marginTop: 8 }} routerLink="/warehouse/production-day">
                  Vai alla Scheda di produzione →
                </IonButton>
                {/* T-432: riapertura per modifiche (solo responsabile), con conferma. */}
                {canDischarge && (
                  <IonButton expand="block" size="small" fill="outline" color="warning" style={{ marginTop: 6 }} disabled={busy} onClick={() => setConfirm('reopen')}>
                    Riapri per modifiche
                  </IonButton>
                )}
              </div>
            )}
            {dischargeNeedsReconciliation && (
              <div
                role="alert"
                style={{
                  background: '#fef2f2',
                  border: '1px solid #dc2626',
                  borderRadius: 12,
                  padding: '10px 14px',
                  marginBottom: 12,
                  fontSize: 13,
                  color: '#991b1b',
                }}
              >
                ⚠️ Lo scarico precedente non è stato confermato dal server ed è
                stato annullato. Il modulo è di nuovo modificabile: controlla
                quantità e lotti, correggi l’errore indicato nella
                sincronizzazione e premi nuovamente <strong>Scarica e chiudi</strong>.
              </div>
            )}
            {savedByChefBanner && (
              <div style={{ background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: 12, padding: '8px 14px', marginBottom: 12, fontSize: 12, color: '#1d4ed8' }}>
                📝 {savedByChefBanner}
              </div>
            )}
            {/* T-441 — una o più varianti dieta attese non sono ancora
                sincronizzate: quelle righe sono escluse dal fabbisogno (non si
                consuma la base al loro posto). Riprova a sincronizzare. */}
            {hasSyncingRows && (
              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12, padding: '8px 14px', marginBottom: 12, fontSize: 12, color: '#b45309' }}>
                ⚠ Alcune varianti dieta non sono ancora sincronizzate: le relative righe sono escluse dal fabbisogno finché non arrivano.
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <IonSpinner />
              </div>
            ) : (
              <>
                <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 4px 10px' }}>
                  {items.length > 0 ? `Piano · ${items.length} preparazioni · ${totalPortions} porzioni` : 'Merce da scaricare'}
                </h2>

                {lines.length === 0 ? (
                  <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontSize: 13 }}>
                    Nessun fabbisogno calcolato per questo giorno/servizio (il piano si compila da &quot;Pianifica
                    produzione&quot;). Puoi comunque aggiungere righe di scarico manuali qui sotto.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* T-457: ricerca prodotto tra le righe di scarico. */}
                    {lines.length > 4 && (
                      <IonSearchbar
                        value={lineSearch}
                        onIonInput={(ev) => setLineSearch(String(ev.detail.value ?? ''))}
                        placeholder="Cerca prodotto…"
                        debounce={0}
                        style={{ padding: 0, '--border-radius': '12px', '--box-shadow': '0 1px 3px rgba(15,23,42,.1)' } as React.CSSProperties}
                      />
                    )}
                    {visibleLines.length === 0 ? (
                      <div style={{ background: '#fff', borderRadius: 14, padding: 14, color: '#64748b', fontSize: 13 }}>
                        Nessun prodotto trovato per &quot;{lineSearch}&quot;.
                      </div>
                    ) : visibleLines.map((l) => {
                      const lots = lotsByFood.get(l.food_id) ?? [];
                      const curInList = l.batch_lot != null && lots.some((x) => (x.batch_lot ?? '') === l.batch_lot);
                      const avail = lots.find((x) => (x.batch_lot ?? '') === (l.batch_lot ?? ''))?.available;
                      const expSoon = l.expiration_date != null && (Date.parse(l.expiration_date) - Date.now()) < 3 * 24 * 60 * 60 * 1000;
                      const isOpen = openLotKey === l.key && !closed;
                      return (
                        <div key={l.key} style={{ background: '#fff', border: '1px solid #cfd8e4', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,.12), 0 6px 16px rgba(15,23,42,.08)' }}>
                          {/* Header: nome + cestino */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '13px 14px 12px' }}>
                            <div style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 800, color: '#0f172a', wordBreak: 'break-word', lineHeight: 1.2 }}>
                              {l.food_name}
                              {l.custom && <span style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fde68a', borderRadius: 999, padding: '2px 7px', marginLeft: 6, whiteSpace: 'nowrap' }}>manuale</span>}
                              {/* T-441: fabbisogno stimato sul BOM della ricetta base
                                  (variante dieta non ancora definita). */}
                              {estimateFoodIds.has(l.food_id) && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 999, padding: '2px 7px', marginLeft: 6, whiteSpace: 'nowrap' }}>stima su base</span>
                              )}
                            </div>
                            {!closed && (
                              <button type="button" onClick={() => removeLine(l.key)} disabled={busy} aria-label="rimuovi riga" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#dc2626', flexShrink: 0 }}>
                                <IonIcon icon={trashOutline} style={{ fontSize: 20 }} />
                              </button>
                            )}
                          </div>

                          <div style={{ borderTop: '1px solid #eef1f6' }}>
                            {/* Sezione LOTTO */}
                            <div style={{ padding: '11px 14px' }}>
                              <button
                                type="button"
                                disabled={closed || busy}
                                onClick={() => setOpenLotKey(isOpen ? null : l.key)}
                                style={{
                                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                                  background: closed ? '#f1f5f9' : '#eff5ff', border: `1.5px solid ${isOpen ? '#2563eb' : '#bcd4f5'}`, borderRadius: 12,
                                  padding: '10px 12px', cursor: closed ? 'default' : 'pointer', textAlign: 'left',
                                }}
                              >
                                <span style={{ minWidth: 0 }}>
                                  <span style={{ display: 'block', fontSize: 10, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lotto</span>
                                  <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: l.batch_lot ? '#0f172a' : '#64748b', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {l.batch_lot ?? 'Scegli un lotto…'}
                                  </span>
                                </span>
                                <IonIcon icon={isOpen ? chevronDownOutline : chevronForwardOutline} style={{ fontSize: 18, color: '#2563eb', flexShrink: 0 }} />
                              </button>

                              {/* Lista lotti in-linea (sostituisce il menu nativo) */}
                              {isOpen && (
                                <div style={{ marginTop: 8, border: '1px solid #cfd8e4', borderRadius: 12, overflow: 'hidden' }}>
                                  {lots.length === 0 && !curInList && (
                                    <div style={{ padding: '11px 12px', fontSize: 13, color: '#94a3b8' }}>Nessun lotto a magazzino per questo alimento.</div>
                                  )}
                                  {lots.map((lot, li) => {
                                    const sel = (lot.batch_lot ?? '') === (l.batch_lot ?? '');
                                    return (
                                      <button key={lot.batch_lot ?? `__${li}`} type="button"
                                        onClick={() => { onSelectLot(l.key, lot.batch_lot ?? ''); setOpenLotKey(null); }}
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', border: 'none', borderTop: li === 0 ? 'none' : '1px solid #eef1f6', background: sel ? '#eff5ff' : '#fff', cursor: 'pointer', textAlign: 'left' }}>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                          <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Lotto {lot.batch_lot ?? '—'}</span>
                                          <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 1 }}>{lot.expiration_date ? `scade ${fmtDate(lot.expiration_date)}` : 'senza scadenza'}</span>
                                        </span>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#334155', background: '#e2e8f0', borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>{fmtNum(lot.available)} {lot.uom}</span>
                                        {sel && <IonIcon icon={checkmarkCircle} style={{ fontSize: 18, color: '#2563eb', flexShrink: 0 }} />}
                                      </button>
                                    );
                                  })}
                                  {l.batch_lot != null && !curInList && (
                                    <button type="button" onClick={() => setOpenLotKey(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', border: 'none', borderTop: lots.length ? '1px solid #eef1f6' : 'none', background: '#eff5ff', cursor: 'pointer', textAlign: 'left' }}>
                                      <span style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Lotto {l.batch_lot}</span>
                                        <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 1 }}>{l.expiration_date ? `scade ${fmtDate(l.expiration_date)}` : 'dalla bozza'}</span>
                                      </span>
                                      <IonIcon icon={checkmarkCircle} style={{ fontSize: 18, color: '#2563eb', flexShrink: 0 }} />
                                    </button>
                                  )}
                                  <button type="button" onClick={() => { onSelectLot(l.key, NONE_LOT); setOpenLotKey(null); }}
                                    style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '11px 12px', border: 'none', borderTop: '1px solid #eef1f6', background: (l.batch_lot == null) ? '#eff5ff' : '#fff', cursor: 'pointer', textAlign: 'left', fontSize: 14, color: '#334155' }}>
                                    Nessun lotto specifico
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Sezione SCADENZA + DISPONIBILE (box distinti con icona) */}
                            <div style={{ padding: '11px 14px', borderTop: '1px solid #eef1f6', display: 'flex', gap: 10 }}>
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, background: expSoon ? '#fef3c7' : '#f1f5f9', border: `1px solid ${expSoon ? '#fcd34d' : '#e2e8f0'}`, borderRadius: 12, padding: '9px 11px', minWidth: 0 }}>
                                <span style={{ fontSize: 18 }}>{expSoon ? '⚠️' : '📅'}</span>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Scadenza</div>
                                  <div style={{ fontSize: 15, fontWeight: 700, color: expSoon ? '#b45309' : '#0f172a', marginTop: 1 }}>{l.expiration_date ? fmtDate(l.expiration_date) : '—'}</div>
                                </div>
                              </div>
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 12, padding: '9px 11px', minWidth: 0 }}>
                                <span style={{ fontSize: 18 }}>📦</span>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Disponibile</div>
                                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginTop: 1 }}>{avail != null ? `${fmtNum(avail)} ${l.uom}` : '—'}</div>
                                </div>
                              </div>
                            </div>

                            {/* Sezione QUANTITÀ da scaricare (fascia evidenziata) */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderTop: '1px solid #eef1f6', background: '#f8fafc' }}>
                              <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#334155' }}>Da scaricare</div>
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                disabled={closed || busy}
                                value={l.qty}
                                placeholder="0"
                                onChange={(ev) => patchLine(l.key, { qty: ev.target.value })}
                                style={{ width: 104, padding: 11, borderRadius: 12, border: '1.5px solid #94a3b8', fontSize: 18, fontWeight: 700, textAlign: 'right', color: '#0f172a', boxSizing: 'border-box', background: closed ? '#f1f5f9' : '#fff' }}
                              />
                              <span style={{ fontSize: 15, fontWeight: 600, color: '#475569', minWidth: 26 }}>{l.uom}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Aggiungi riga manuale: selettore con ricerca (tutti gli alimenti) */}
                {!closed && (
                  <div style={{ marginTop: 12, background: '#fff', border: '1px dashed #a9b7cc', borderRadius: 14, padding: '2px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                    <PickerTriggerField
                      label="＋ Aggiungi articolo da scaricare"
                      placeholder="Cerca e scegli un alimento…"
                      onClick={() => setPickerOpen(true)}
                    />
                  </div>
                )}

                {/* Azioni: Salva (tutti) + Scarica&chiudi (solo responsabile) */}
                {!closed && lines.length > 0 && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                    <IonButton expand="block" fill="outline" style={{ flex: 1 }} disabled={busy} onClick={() => { void doSave(); }}>
                      <IonIcon icon={saveOutline} slot="start" />
                      Salva
                    </IonButton>
                    {canDischarge && (
                      <IonButton expand="block" color="success" style={{ flex: 1 }} disabled={busy} onClick={() => setConfirm('discharge')}>
                        <IonIcon icon={checkmarkDoneOutline} slot="start" />
                        Scarica e chiudi
                      </IonButton>
                    )}
                  </div>
                )}
                {busy && (
                  <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <IonSpinner name="dots" />
                  </div>
                )}
                {!canDischarge && !closed && (
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: '12px 4px 0' }}>
                    Salva la preparazione dello scarico: il responsabile la verificherà e chiuderà il modulo
                    (scarico effettivo dal magazzino).
                  </p>
                )}
                <p style={{ fontSize: 11, color: '#94a3b8', margin: '10px 4px 0' }}>
                  Lotti proposti in ordine <strong>FIFO</strong> (scadenza più vicina prima). Quantità e lotto sono
                  modificabili; aggiungi righe per articoli mancanti o se un lotto si esaurisce.
                </p>
              </>
            )}
          </div>
        )}

        <IonAlert
          isOpen={confirm === 'discharge'}
          header="Scarica e chiudi"
          message={`Scaricare dal magazzino le ${lines.filter((l) => Number((l.qty || '0').replace(',', '.')) > 0).length} righe con quantità e chiudere il modulo del giorno? L'operazione è registrata (append-only) e non è più modificabile.`}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Scarica e chiudi', handler: () => { void doDischarge(); } },
          ]}
          onDidDismiss={() => setConfirm(null)}
        />

        {/* T-432 — conferma riapertura scarico */}
        <IonAlert
          isOpen={confirm === 'reopen'}
          header="Riaprire lo scarico?"
          message={`Le ${(savedDraft?.lines ?? []).filter((l) => (Number(l.qty) || 0) > 0).length} righe già scaricate verranno STORNATE (la giacenza torna al valore precedente) e il modulo torna modificabile. Lo storno è registrato (append-only). Al termine ricordati di "Scarica e chiudi" di nuovo.`}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Riapri e storna', handler: () => { void doReopen(); } },
          ]}
          onDidDismiss={() => setConfirm(null)}
        />

        <SearchPickerModal
          isOpen={pickerOpen}
          title="Aggiungi articolo"
          wrapItems
          focusSearchOnOpen
          items={foodPickerItems}
          searchPlaceholder="Cerca alimento…"
          emptyHint="Nessun alimento in anagrafica."
          onSelect={(id) => addCustomLine(id)}
          onClose={() => setPickerOpen(false)}
        />

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2400} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
